import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import ErrorWithCountdown from '#/shared/components/ErrorWithCountdown';
import useNavigate from '#/shared/hooks/useNavigate';
import RouteDetailHeader from '#/transit-tracker/components/RouteDetailHeader';
import RouteServiceAlertsPreview from '#/transit-tracker/components/RouteServiceAlertsPreview';
import RouteDirectionsTabs from '#/transit-tracker/components/RouteDirectionsTabs';
import BasicStopsList from '#/transit-tracker/components/BasicStopsList';
import DetailViewLayout from '#/components/DetailViewLayout';
import DetailNavHeader from '#/shared/components/DetailNavHeader';
import { DefaultBlock } from '#/shared/components/Styled';
import SEO from '#/shared/components/SEO';
import {
  useGetRouteAlertsQuery,
  useGetRouteDetailsQuery,
} from '#/services/developerApi';
import useViewMapControls from '#/map/hooks/useViewMapControls';
import ROUTES from '#/constants/routes';
import useTriMetStaticPageLinks from '#/shared/hooks/useTriMetStaticPageLinks';

const RouteContentContainer = styled.div`
  background-color: ${props => props.theme.backgroundColor};
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const RouteNotFoundContainer = styled.div`
  display: flex;
  justify-content: center;
  flex: 1;
  padding: 10px;
  font-size: 1.2em;
`;

function RouteDetailContainer({ routeId }) {
  const { navigate, generateNavigateBack, generateNavigateClose } =
    useNavigate();

  const { data: alertsData } = useGetRouteAlertsQuery(routeId);
  const routeSpecificAlerts = alertsData?.routeAlerts?.alerts;

  const {
    data: routeDetail,
    isLoading,
    isError,
  } = useGetRouteDetailsQuery({ route: routeId, stops: true });

  const { routeDetailViewOpened, viewClosed } = useViewMapControls();

  useEffect(() => {
    routeDetailViewOpened();
    return viewClosed;
  }, [routeDetailViewOpened, viewClosed]);

  const [routeDirection, setRouteDirection] = useState(0);

  const stopListRef = useRef(null);

  const navigateToStop = stopId => {
    navigate(ROUTES.stopByIdAndRoute, stopId, routeDetail?.id);
  };

  const { getRouteScheduleLink } = useTriMetStaticPageLinks();

  const navigateToFullRouteDetails = useCallback(() => {
    window.open(getRouteScheduleLink(routeDetail?.id), '_blank');
  }, [getRouteScheduleLink, routeDetail?.id]);

  const navigateToRouteAlerts = useCallback(() => {
    navigate(ROUTES.alertsById, routeId);
  }, [routeId, navigate]);

  const navigateToRouteAlertsEmail = useCallback(() => {
    const focusEmail = true;
    navigate(ROUTES.alertsById, routeId, focusEmail);
  }, [routeId, navigate]);

  const routeDirectionData = routeDetail?.dir;
  const selectedDirectionStops = routeDirectionData?.[routeDirection]?.stop;

  return (
    <>
      {routeDetail && (
        <SEO
          metadataId="routeDetail"
          dynamicValues={{
            routeDescription: routeDetail.desc,
            routeNumber: routeDetail.id,
          }}
        />
      )}
      <DetailViewLayout
        top={
          <DetailNavHeader
            onBack={generateNavigateBack()}
            onClose={generateNavigateClose()}
            content={routeDetail?.desc}
          />
        }
        bottom={
          (isError && (
            <ErrorWithCountdown errorMessageId="system-error.warning" />
          )) || (
            <RouteContentContainer>
              {!isLoading && !routeDetail && (
                <RouteNotFoundContainer>
                  <FormattedMessage id="detailview.route-not-found" />
                </RouteNotFoundContainer>
              )}
              {routeDetail && (
                <>
                  <RouteDetailHeader
                    routeDetail={routeDetail}
                    navigateToFullRouteDetails={navigateToFullRouteDetails}
                  >
                    {routeSpecificAlerts && (
                      <RouteServiceAlertsPreview
                        routeAlerts={routeSpecificAlerts}
                        navigateToRouteAlerts={navigateToRouteAlerts}
                        navigateToRouteAlertsEmail={navigateToRouteAlertsEmail}
                        routeIsShuttle={routeDetail.isShuttle}
                      />
                    )}
                  </RouteDetailHeader>
                  {routeDirectionData?.length && (
                    <RouteDirectionsTabs
                      directions={routeDirectionData}
                      activeDirection={routeDirection}
                      setActiveDirection={setRouteDirection}
                    />
                  )}
                </>
              )}
              {!routeDetail && <DefaultBlock />}
              {selectedDirectionStops?.length && (
                <BasicStopsList
                  stops={selectedDirectionStops}
                  navigateToStop={navigateToStop}
                  bodyRef={stopListRef}
                />
              )}
            </RouteContentContainer>
          )
        }
      />
    </>
  );
}

RouteDetailContainer.propTypes = {
  routeId: PropTypes.string,
};

RouteDetailContainer.defaultProps = {
  routeId: null,
};

export default RouteDetailContainer;
